import styled, {css} from 'styled-components';

import colors from '@/theme/constants/colors';
import container from '@/theme/constants/container';
import {sizePx} from '@/theme/helpers/size';
import typography from '@/theme/typography';
import focus from '@/theme/focus';
import zIndex from '@/theme/constants/zIndex';
import type {StyledLinkProps, StyledMegaMenuProps, StyledTopLinkProps} from './typings';
import breakpoints from '@/theme/constants/breakpoints';
import pxToRem from '@/theme/helpers/pxToRem';
import {StyledProfileMenu} from '@/molecules/Fragments/Menu/Navigation/Profile/styles';

const background = colors.grey25;
const containerDiv = css`
  max-width: ${container.maxWidth};
  width: 100%;
`;
const ArrowStyles = css`
  &::after {
    content: '';
    display: block;
    height: 0;
    width: 0;
    border: 12px inset transparent;
    border-bottom: 12px solid #fff;
    position: absolute;
    bottom: 0;
    left: 50%;
    margin-left: -12px;
  }
`;

export const StyledDesktopNavigation = styled.div`
  display: none;
  width: 100%;
  background: ${background};

  @media only screen and ${breakpoints.device.l} {
    display: block;
  }
`;

export const StyledMainElements = styled.div`
  height: ${sizePx(35)};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 ${sizePx(8)};
  margin: 0 auto;
  ${containerDiv}

  a[aria-label='Go to Artfinder homepage'] {
    margin-right: auto;
    height: ${sizePx(25)};
    padding: 0 ${sizePx(5)};
    margin-left: -${sizePx(5)};
    border-radius: ${sizePx(4)};
    ${focus.brand()};
  }

  > a:not([aria-label='Go to Artfinder homepage']),
  > span {
    width: ${sizePx(23)};
    height: 100%;
  }

  & > span {
    position: relative;
  }

  ${StyledProfileMenu} {
    z-index: ${zIndex.dropdown};
  }

  & > span > ul {
    display: none;
    background: ${colors.white};
    box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.1);
    position: absolute;
    width: max-content;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    padding: 0;
    z-index: ${zIndex.dropdown};

    ${ArrowStyles}
    &::after {
      bottom: unset;
      top: -${sizePx(12)};
    }

    > li {
      display: flex;
      align-items: center;

      &:hover {
        background: ${colors.grey25};
      }

      > a,
      > span {
        height: 48px;
        padding: 0 24px;
        display: flex;
        align-items: center;
        flex-grow: 1;
        cursor: pointer;

        &:hover {
          color: ${colors.darkBlue100};
        }
      }
    }
  }

  & > span:hover > ul {
    display: block;
  }

  & > span > a:focus-within,
  & > a:focus-within {
    ${focus.none}
    color: ${colors.red100};
  }

  & > span > a,
  & > a {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    &:hover {
      color: ${colors.red100};
    }
  }
`;

export const StyledNavigation = styled.div`
  display: flex;
  justify-content: center;
  height: ${sizePx(23)};
  width: 100%;
  border-top: 1px solid ${colors.grey50};
  box-sizing: content-box;

  > ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
  }
`;

export const StyledMegaMenu = styled.div<StyledMegaMenuProps>`
  display: ${(props) => (props.$isActive ? 'block' : 'none')};
  background: ${colors.white};
  box-shadow: 0px 5px 5px rgba(0, 0, 0, 0.1);
  position: absolute;
  left: 0;
  right: 0;
  width: 100%;
  z-index: ${zIndex.dropdown};
  flex-direction: row;
  margin: 0;
  padding: 0;

  figure {
    flex: 1 1 0;
    width: 0;
    max-width: ${sizePx(137)};
    margin: 0;
  }

  figcaption {
    ${typography.Headline}
    height: ${pxToRem(20)};
    margin-bottom: ${sizePx(10)};
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  > div {
    ${containerDiv}
    display: flex;
    margin: 0 auto;
    padding: ${sizePx(16)} ${sizePx(6)};
    gap: ${sizePx(12)};
  }
`;

export const StyledTopLink = styled.a<StyledTopLinkProps>`
  ${typography.Subtitle2}
  display: inline-block;
  padding: ${sizePx(6)} ${sizePx(4)};
  color: ${colors.darkBlue100};
  position: relative;

  ${(props) => props.$hasArrow && ArrowStyles}

  &:hover {
    color: ${colors.red100};
    ${(props) => props.$hasArrow && ArrowStyles}
  }

  ${focus.brand()}
`;

export const StyledDivider = styled.div`
  height: 1px;
  width: 100%;
  background: ${colors.grey100};
  margin: ${sizePx(8)} 0;
`;

export const StyledListItem = styled.li<StyledLinkProps>`
  ${(props) => (!!props.$bold ? typography.Subtitle1 : typography.Body1)}

  &:not(:last-child) {
    margin-bottom: ${sizePx(4)};
  }

  a {
    display: inline-block;
    border-radius: ${sizePx(2)};
    ${focus.brand()}

    &:hover {
      color: ${colors.darkBlue50};
    }
  }
`;
